/**
 * topSightsEnrichment.ts
 *
 * Pure async helpers ported from TopSightsCarousel.js.
 * No React / Zustand dependency – just functions that fetch, parse and enrich.
 */

import { functionUrls } from '../../../services/firebaseFunctions';
import { loginMachine } from '../../../auth/loginMachine';
import type { NamedImageItem } from '../types/detailsCard.types';

const WIKI_API_PATH = '/w/api.php';

// ---------------------------------------------------------------------------
// In-memory cache  (same key format as original: `wikiLang|userLangLabel|topicName`)
// ---------------------------------------------------------------------------
const TOP_SIGHTS_CACHE = new Map<string, NamedImageItem[]>();
const ARTICLE_PAGEVIEWS_CACHE = new Map<string, number | null>();

export function makeCacheKey(
  viewportWikiLanguage: string,
  userLanguageLabel: string,
  topicName: string,
): string {
  return `${viewportWikiLanguage}|${userLanguageLabel}|${topicName}`;
}

export function getCachedSights(key: string): NamedImageItem[] | null {
  return TOP_SIGHTS_CACHE.has(key) ? TOP_SIGHTS_CACHE.get(key)! : null;
}

export function setCachedSights(key: string, items: NamedImageItem[]): void {
  TOP_SIGHTS_CACHE.set(key, items);
}

// ---------------------------------------------------------------------------
// CSV parsing  (handles quoted fields – identical to original)
// ---------------------------------------------------------------------------
export function parseCsvLine(lineStr: string): [string, string, string, string] {
  const result: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < lineStr.length; i++) {
    const ch = lineStr[i];
    if (ch === '"') {
      if (inQuotes && lineStr[i + 1] === '"') {
        field += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(field.trim());
      field = '';
    } else {
      field += ch;
    }
  }
  result.push(field.trim());
  return [result[0] || '', result[1] || '', result[2] || '', result[3] || ''];
}

// ---------------------------------------------------------------------------
// Haversine distance
// ---------------------------------------------------------------------------
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ---------------------------------------------------------------------------
// Wikipedia helpers
// ---------------------------------------------------------------------------
async function searchWikipedia(
  searchTerm: string,
  langCode: string,
  signal: AbortSignal,
): Promise<Array<{ pageid: number; title: string }>> {
  const params = new URLSearchParams({
    action: 'query',
    list: 'search',
    srsearch: searchTerm,
    srlimit: '5',
    format: 'json',
    origin: '*',
  });
  const apiUrl = `https://${langCode}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`;
  try {
    const resp = await fetch(apiUrl, { signal });
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    return (data.query?.search ?? []) as Array<{ pageid: number; title: string }>;
  } catch (err: any) {
    if (err.name !== 'AbortError') console.error('[TopSights][searchWikipedia] error', err);
    return [];
  }
}

const ARTICLE_LANG_LINKS_CACHE = new Map<string, Array<{ lang: string; title: string }>>();

function buildWikipediaArticleUrl(langCode: string, articleTitle: string): string {
  return `https://${langCode}.wikipedia.org/wiki/${encodeURIComponent(
    String(articleTitle || '').replace(/ /g, '_'),
  )}`;
}

async function fetchArticleLangLinks(
  title: string,
  sourceArticleLang: string,
  signal: AbortSignal,
): Promise<Array<{ lang: string; title: string }>> {
  const cacheKey = `${sourceArticleLang}:${String(title || '').trim().toLowerCase()}`;
  if (ARTICLE_LANG_LINKS_CACHE.has(cacheKey)) {
    return ARTICLE_LANG_LINKS_CACHE.get(cacheKey) || [];
  }

  const base = `https://${sourceArticleLang}.wikipedia.org/w/api.php`;
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'langlinks',
    lllimit: 'max',
    format: 'json',
    origin: '*',
  });

  const resp = await fetch(`${base}?${params.toString()}`, { signal });
  if (!resp.ok) throw new Error(`Wikipedia API error (langlinks): ${resp.status}`);

  const data = await resp.json();
  const pages = data.query?.pages;
  if (!pages) return [];

  const pageId = Object.keys(pages)[0];
  if (!pageId || !pages[pageId].langlinks) return [];

  const links = pages[pageId].langlinks.map((link: any) => ({
    lang: link.lang,
    title: link['*'],
  }));

  ARTICLE_LANG_LINKS_CACHE.set(cacheKey, links);
  return links;
}

async function resolvePreferredArticleTitle(params: {
  sourceTitle: string;
  sourceLang: string;
  preferredLang: string;
  signal: AbortSignal;
}): Promise<{ lang: string; title: string }> {
  const { sourceTitle, sourceLang, preferredLang, signal } = params;
  const normalizedTitle = String(sourceTitle || '').trim();
  const normalizedSourceLang = String(sourceLang || '').trim() || 'en';
  const normalizedPreferredLang = String(preferredLang || '').trim() || 'en';

  if (!normalizedTitle) return { lang: normalizedSourceLang, title: normalizedTitle };
  if (normalizedPreferredLang === normalizedSourceLang) {
    return { lang: normalizedSourceLang, title: normalizedTitle };
  }

  try {
    const links = await fetchArticleLangLinks(normalizedTitle, normalizedSourceLang, signal);

    const inPreferredLanguage = links.find((l) => l.lang === normalizedPreferredLang);
    if (inPreferredLanguage) {
      return { lang: normalizedPreferredLang, title: inPreferredLanguage.title };
    }

    const inEnglish = links.find((l) => l.lang === 'en');
    if (inEnglish) {
      return { lang: 'en', title: inEnglish.title };
    }

    return { lang: normalizedSourceLang, title: normalizedTitle };
  } catch {
    return { lang: normalizedSourceLang, title: normalizedTitle };
  }
}

async function getCoordinatesForPageIds(
  pageIds: number[],
  langCode: string,
  signal: AbortSignal,
): Promise<Record<string, any>> {
  if (!pageIds.length) return {};
  const params = new URLSearchParams({
    action: 'query',
    pageids: pageIds.join('|'),
    prop: 'coordinates',
    format: 'json',
    origin: '*',
  });
  const apiUrl = `https://${langCode}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`;
  try {
    const resp = await fetch(apiUrl, { signal });
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    return data.query?.pages ?? {};
  } catch (err: any) {
    if (err.name !== 'AbortError') console.error('[TopSights][getCoords] error', err);
    return {};
  }
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function computePageviewsSum(page: any): number | null {
  const pageviews = page?.pageviews;
  if (!pageviews || typeof pageviews !== 'object') return null;

  let sawValue = false;
  const total = Object.values(pageviews).reduce<number>((sum, value) => {
    const views = toFiniteNumber(value);
    if (views == null) return sum;
    sawValue = true;
    return sum + views;
  }, 0);

  return sawValue ? total : null;
}

async function fetchArticlePageviewsSum(
  title: string,
  langCode: string,
  signal: AbortSignal,
): Promise<number | null> {
  const normalizedTitle = String(title || '').trim();
  const normalizedLang = String(langCode || '').trim() || 'en';
  if (!normalizedTitle) return null;

  const cacheKey = `${normalizedLang}:${normalizedTitle.toLowerCase()}`;
  if (ARTICLE_PAGEVIEWS_CACHE.has(cacheKey)) {
    return ARTICLE_PAGEVIEWS_CACHE.get(cacheKey) ?? null;
  }

  const params = new URLSearchParams({
    action: 'query',
    titles: normalizedTitle,
    prop: 'pageviews',
    format: 'json',
    origin: '*',
  });

  try {
    const resp = await fetch(`https://${normalizedLang}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`, { signal });
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const data = await resp.json();
    const pages = data.query?.pages ?? {};
    const pageId = Object.keys(pages)[0];
    const views = computePageviewsSum(pages[pageId] ?? {});
    ARTICLE_PAGEVIEWS_CACHE.set(cacheKey, views);
    return views;
  } catch (err: any) {
    if (err.name !== 'AbortError') console.warn('[TopSights][fetchArticlePageviewsSum] error', err);
    ARTICLE_PAGEVIEWS_CACHE.set(cacheKey, null);
    return null;
  }
}

function readExistingWikiViews(item: NamedImageItem): number | null {
  for (const value of [item?.wikiViews, item?.pvSum]) {
    const views = toFiniteNumber(value);
    if (views != null) return views;
  }
  return null;
}

async function fetchThumbAndCoords(
  identifier: string,
  langCode: string,
  isPageId: boolean,
  signal: AbortSignal,
): Promise<{ thumb: string | null; coords: { lat: number; lon: number } | null; title: string | null; articleSizeBytes: number | null; wikiViews: number | null }> {
  const params = new URLSearchParams({
    action: 'query',
    prop: 'pageimages|coordinates|info|pageviews',
    piprop: 'thumbnail',
    pithumbsize: '200',
    format: 'json',
    origin: '*',
  });
  if (isPageId) params.set('pageids', identifier);
  else params.set('titles', identifier);

  const apiUrl = `https://${langCode}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`;
  try {
    const resp = await fetch(apiUrl, { signal });
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    const pages = data.query?.pages ?? {};
    const pageId = Object.keys(pages)[0];
    const page = pages[pageId] ?? {};
    const thumb: string | null = page.thumbnail?.source ?? null;
    const c = page.coordinates?.[0];
    const coords =
      c && typeof c.lat === 'number' && typeof c.lon === 'number'
        ? { lat: c.lat as number, lon: c.lon as number }
        : null;
    const normalizedArticleSize = Number(page?.length ?? NaN);
    const wikiViews = computePageviewsSum(page);
    return {
      thumb,
      coords,
      title: page.title ?? null,
      articleSizeBytes: Number.isFinite(normalizedArticleSize) && normalizedArticleSize > 0
        ? normalizedArticleSize
        : null,
      wikiViews,
    };
  } catch (err: any) {
    if (err.name !== 'AbortError') console.error('[TopSights][fetchThumbAndCoords] error', err);
    return { thumb: null, coords: null, title: isPageId ? null : identifier, articleSizeBytes: null, wikiViews: null };
  }
}

function normalizeWikidataTag(rawTag: string): string {
  const clean = String(rawTag || '').trim().toUpperCase();
  return /^Q\d+$/.test(clean) ? clean : '';
}

async function resolveWikipediaSitelinkFromWikidata(
  wikidataTag: string,
  preferredLangs: string[],
  signal: AbortSignal,
): Promise<{ lang: string; title: string } | null> {
  const cleanTag = normalizeWikidataTag(wikidataTag);
  if (!cleanTag) return null;

  const params = new URLSearchParams({
    action: 'wbgetentities',
    format: 'json',
    ids: cleanTag,
    props: 'sitelinks',
    origin: '*',
  });

  const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
  const resp = await fetch(url, { signal });
  if (!resp.ok) {
    throw new Error(`Wikidata sitelinks lookup failed: HTTP ${resp.status}`);
  }

  const data = await resp.json();
  const sitelinks = data?.entities?.[cleanTag]?.sitelinks || {};

  for (const lang of preferredLangs) {
    const key = `${String(lang || '').trim().toLowerCase()}wiki`;
    const title = String(sitelinks?.[key]?.title || '').trim();
    if (title) return { lang: String(lang || '').trim().toLowerCase(), title };
  }

  // Final fallback: take any wikipedia sitelink.
  for (const [key, value] of Object.entries(sitelinks)) {
    if (!String(key).endsWith('wiki')) continue;
    const lang = String(key).replace(/wiki$/, '').toLowerCase();
    const title = String((value as any)?.title || '').trim();
    if (lang && title) return { lang, title };
  }

  return null;
}

// ---------------------------------------------------------------------------
// enrichSight – identical logic to original, typed
// ---------------------------------------------------------------------------
export interface EnrichedSight {
  link: string;
  title: string;
  description: string;
  thumbnail: string | null;
  coords: { lat: number; lon: number } | null;
  sourceWikiLang: string;
  articleSizeBytes: number | null;
  wikiViews: number | null;
  wikidataTag?: string;
}

export async function enrichSight(
  sight: { link: string; title: string; description: string; wikidataTag?: string },
  mapCenterLat: number,
  mapCenterLon: number,
  viewportWikiLanguage: string,
  preferredWikiLanguage: string,
  signal: AbortSignal,
): Promise<EnrichedSight> {
  let thumbnailUrl: string | null = null;
  let coordsObj: { lat: number; lon: number } | null = null;
  let resolvedTitle = sight.title;
  let resolvedWikiLang = viewportWikiLanguage;
  let resolvedArticleSizeBytes: number | null = null;
  let resolvedWikiViews: number | null = null;

  const debug = {
    title: sight.title,
    link: sight.link,
    wikidataTag: normalizeWikidataTag(sight.wikidataTag || ''),
    hadLinkTitle: false,
    usedSlugSearch: false,
    usedTitleSearch: false,
    usedWikidataFallback: false,
    wikidataResolved: null as null | { lang: string; title: string },
  };

  const tryFullTextSearch = async (term: string, langCode: string): Promise<boolean> => {
    const searchResults = await searchWikipedia(term, langCode, signal);
    if (!searchResults.length) return false;
    const pageIds = searchResults.map((r) => r.pageid);
    const coordsData = await getCoordinatesForPageIds(pageIds, langCode, signal);
    for (const res of searchResults) {
      if (signal.aborted) return false;
      const coord = coordsData[res.pageid]?.coordinates?.[0];
      if (!coord) continue;
      if (haversineKm(mapCenterLat, mapCenterLon, coord.lat, coord.lon) > 40) continue;
      const tc = await fetchThumbAndCoords(res.title, langCode, false, signal);
      if (tc.thumb) {
        thumbnailUrl = tc.thumb;
        coordsObj = tc.coords ?? { lat: coord.lat, lon: coord.lon };
        resolvedTitle = tc.title ?? res.title;
        resolvedArticleSizeBytes = tc.articleSizeBytes ?? resolvedArticleSizeBytes;
        resolvedWikiViews = tc.wikiViews ?? resolvedWikiViews;
        return true;
      }
    }
    return false;
  };

  try {
    let langFromLink = viewportWikiLanguage;
    let titleFromLink: string | null = null;
    let pageIdFromLink: string | null = null;

    try {
      const parsedUrl = new URL(sight.link);
      langFromLink = parsedUrl.hostname.split('.')[0] || viewportWikiLanguage;
      titleFromLink =
        decodeURIComponent(parsedUrl.pathname.split('/wiki/')[1] || '')
          .replace(/_/g, ' ')
          .trim() || null;
      const qp = new URLSearchParams(parsedUrl.search);
      if (qp.has('curid')) pageIdFromLink = qp.get('curid');
    } catch {
      langFromLink = viewportWikiLanguage;
    }

    resolvedWikiLang = langFromLink || viewportWikiLanguage;

    // Attempt A – pageID or title from link
    if (pageIdFromLink || titleFromLink) {
      debug.hadLinkTitle = true;
      const tc = await fetchThumbAndCoords(
        (pageIdFromLink || titleFromLink)!,
        langFromLink,
        !!pageIdFromLink,
        signal,
      );
      if (tc.thumb) {
        thumbnailUrl = tc.thumb;
        coordsObj = tc.coords;
        resolvedTitle = tc.title ?? resolvedTitle;
        resolvedArticleSizeBytes = tc.articleSizeBytes ?? resolvedArticleSizeBytes;
        resolvedWikiViews = tc.wikiViews ?? resolvedWikiViews;
      } else if (tc.coords) {
        coordsObj = tc.coords;
        resolvedTitle = tc.title ?? resolvedTitle;
        resolvedArticleSizeBytes = tc.articleSizeBytes ?? resolvedArticleSizeBytes;
        resolvedWikiViews = tc.wikiViews ?? resolvedWikiViews;
      }
    }

    // Attempt B – full text search with slug
    if (!thumbnailUrl && titleFromLink) {
      debug.usedSlugSearch = true;
      await tryFullTextSearch(titleFromLink, langFromLink);
    }

    // Attempt C – full text search with CSV title
    if (!thumbnailUrl) {
      debug.usedTitleSearch = true;
      await tryFullTextSearch(sight.title, langFromLink);
    }

    // Attempt D – Wikidata sitelinks fallback (if LLM provided a QID)
    if (!thumbnailUrl) {
      const wikidataTag = normalizeWikidataTag(sight.wikidataTag || '');
      if (wikidataTag) {
        debug.usedWikidataFallback = true;
        try {
          const preferredLangs = Array.from(new Set([
            String(preferredWikiLanguage || '').trim().toLowerCase(),
            String(langFromLink || '').trim().toLowerCase(),
            String(viewportWikiLanguage || '').trim().toLowerCase(),
            'en',
          ].filter(Boolean)));

          const sitelink = await resolveWikipediaSitelinkFromWikidata(wikidataTag, preferredLangs, signal);
          if (sitelink?.title) {
            debug.wikidataResolved = { lang: sitelink.lang, title: sitelink.title };
            const tc = await fetchThumbAndCoords(sitelink.title, sitelink.lang, false, signal);
            if (tc.thumb) {
              thumbnailUrl = tc.thumb;
              coordsObj = tc.coords;
              resolvedWikiLang = sitelink.lang;
              resolvedTitle = tc.title ?? sitelink.title;
              resolvedArticleSizeBytes = tc.articleSizeBytes ?? resolvedArticleSizeBytes;
              resolvedWikiViews = tc.wikiViews ?? resolvedWikiViews;
            }
          }
        } catch (wikidataErr: any) {
          if (wikidataErr?.name !== 'AbortError') {
            console.warn('[TopSights][enrichSight] Wikidata fallback failed', {
              wikidataTag,
              title: sight.title,
              error: wikidataErr?.message || String(wikidataErr),
            });
          }
        }
      }
    }

    // Fallback – thumbnail but no coords
    if (thumbnailUrl && !coordsObj) {
      const tc = await fetchThumbAndCoords(resolvedTitle, resolvedWikiLang || langFromLink, false, signal);
      coordsObj = tc.coords;
      resolvedWikiViews = tc.wikiViews ?? resolvedWikiViews;
    }
  } catch (err: any) {
    if (err.name !== 'AbortError') console.error('[TopSights][enrichSight] error', err);
  }

  const baseTitle = String(resolvedTitle || sight.title || '').trim();
  const baseLang = String(resolvedWikiLang || viewportWikiLanguage || 'en').trim() || 'en';
  const preferredLang = String(preferredWikiLanguage || 'en').trim() || 'en';

  const localized = await resolvePreferredArticleTitle({
    sourceTitle: baseTitle,
    sourceLang: baseLang,
    preferredLang,
    signal,
  });

  const finalLang = localized.lang || baseLang;
  const finalTitle = localized.title || baseTitle;
  const finalLink = buildWikipediaArticleUrl(finalLang, finalTitle);
  const finalWikiViews = resolvedWikiViews ?? await fetchArticlePageviewsSum(finalTitle, finalLang, signal);

  if (!thumbnailUrl) {
    console.warn('[TopSights][enrichSight] no thumbnail resolved', {
      title: sight.title,
      link: sight.link,
      wikidataTag: debug.wikidataTag || null,
      hadLinkTitle: debug.hadLinkTitle,
      usedSlugSearch: debug.usedSlugSearch,
      usedTitleSearch: debug.usedTitleSearch,
      usedWikidataFallback: debug.usedWikidataFallback,
      wikidataResolved: debug.wikidataResolved,
      finalTitle,
      finalLang,
      hasCoords: !!coordsObj,
    });
  }

  return {
    link: finalLink,
    title: finalTitle,
    description: sight.description,
    thumbnail: thumbnailUrl,
    coords: coordsObj,
    sourceWikiLang: finalLang,
    articleSizeBytes: resolvedArticleSizeBytes,
    wikiViews: finalWikiViews,
    wikidataTag: normalizeWikidataTag(sight.wikidataTag || '') || undefined,
  };
}

// ---------------------------------------------------------------------------
// enrichedSightToNamedImageItem – convert to our store type
// ---------------------------------------------------------------------------
export function toNamedImageItem(sight: EnrichedSight): NamedImageItem | null {
  if (!sight.thumbnail) return null;
  return {
    id: sight.link,
    label: sight.title,
    title: sight.title,
    articleTitle: sight.title,
    imageUrl: sight.thumbnail,
    thumbnail: sight.thumbnail,
    lat: sight.coords?.lat,
    lon: sight.coords?.lon,
    link: sight.link,
    sourceWikiLang: sight.sourceWikiLang,
    description: sight.description,
    wikiViews: sight.wikiViews,
    pvSum: sight.wikiViews,
    articleSizeBytes: sight.articleSizeBytes,
    wikidataTag: sight.wikidataTag,
  };
}

export async function localizeNamedImageItem(params: {
  item: NamedImageItem;
  viewportWikiLanguage: string;
  userPreferredWikiLangCode?: string;
  signal: AbortSignal;
}): Promise<NamedImageItem> {
  const { item, viewportWikiLanguage, userPreferredWikiLangCode, signal } = params;
  const sourceTitle = String(item.articleTitle || item.title || item.label || '').trim();
  const sourceLang = String(item.sourceWikiLang || viewportWikiLanguage || 'en').trim() || 'en';
  const preferredLang = String(userPreferredWikiLangCode || viewportWikiLanguage || 'en').trim() || 'en';

  if (!sourceTitle) return item;

  const localized = await resolvePreferredArticleTitle({
    sourceTitle,
    sourceLang,
    preferredLang,
    signal,
  });

  const finalLang = localized.lang || sourceLang;
  const finalTitle = localized.title || sourceTitle;
  const existingViews = readExistingWikiViews(item);
  const wikiViews = existingViews ?? await fetchArticlePageviewsSum(finalTitle, finalLang, signal);

  return {
    ...item,
    label: finalTitle,
    title: finalTitle,
    articleTitle: finalTitle,
    sourceWikiLang: finalLang,
    link: buildWikipediaArticleUrl(finalLang, finalTitle),
    wikiViews,
    pvSum: wikiViews,
  };
}

export async function localizeNamedImageItems(params: {
  items: NamedImageItem[];
  viewportWikiLanguage: string;
  userPreferredWikiLangCode?: string;
  signal: AbortSignal;
}): Promise<NamedImageItem[]> {
  const { items, viewportWikiLanguage, userPreferredWikiLangCode, signal } = params;

  return Promise.all(
    (items || []).map((item) =>
      localizeNamedImageItem({
        item,
        viewportWikiLanguage,
        userPreferredWikiLangCode,
        signal,
      }),
    ),
  );
}

// ---------------------------------------------------------------------------
// streamTopSightsCSV – streams the CSV endpoint and yields enriched sights
// ---------------------------------------------------------------------------
export interface StreamTopSightsParams {
  topicName: string;
  lat: number;
  lng: number;
  viewportWikiLanguage: string;
  userLanguageLabel: string;
  userPreferredWikiLangCode?: string;
  signal: AbortSignal;
  onItem: (item: NamedImageItem) => void;
  onRawItem?: (item: NamedImageItem) => void;
}

function isValidTopSightCsvItem(link: string, title: string, wikidataTag?: string): boolean {
  const cleanLink = String(link || '').trim();
  const cleanTitle = String(title || '').trim();
  const cleanWikidataTag = normalizeWikidataTag(wikidataTag || '');

  if (!cleanTitle) return false;

  // Drop obvious non-article / instruction-like rows.
  if (/^(prompt|instruction|instructions|output|csv)$/i.test(cleanTitle)) return false;

  if (!cleanLink) {
    return !!cleanWikidataTag;
  }

  let parsed: URL;
  try {
    parsed = new URL(cleanLink);
  } catch {
    return !!cleanWikidataTag;
  }

  const host = String(parsed.hostname || '').toLowerCase();
  if (!host.endsWith('wikipedia.org')) return !!cleanWikidataTag;

  const hasWikiPath = String(parsed.pathname || '').includes('/wiki/');
  const hasCurId = parsed.searchParams.has('curid');
  return hasWikiPath || hasCurId || !!cleanWikidataTag;
}

export async function streamTopSightsCSV(params: StreamTopSightsParams): Promise<NamedImageItem[]> {
  const {
    topicName,
    lat,
    lng,
    viewportWikiLanguage,
    userLanguageLabel,
    userPreferredWikiLangCode,
    signal,
    onItem,
    onRawItem,
  } = params;

  loginMachine.recordUsage('llm');

  let idToken: string | null = null;
  try {
    idToken = await loginMachine.getIdToken();
  } catch (error) {
    console.error('[TopSights] Error fetching ID token:', error);
    throw new Error('could not connect to authentication service');
  }

  const gps = Number.isFinite(lat) && Number.isFinite(lng) ? `${lat},${lng}` : '';

  const urlParams = new URLSearchParams({
    location: topicName,
    gpslatlon: gps,
    userlangSpelldout: userLanguageLabel,
    wikiLang: viewportWikiLanguage,
    fromSearchfield: 'false',
  });

  const url = `${functionUrls.getLocationSightsGemini31FlashLitePreview}?${urlParams.toString()}`;
  console.log(`[TopSights] CSV URL: ${url}`);

  const headers: Record<string, string> = { Accept: 'text/plain' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;

  const resp = await fetch(url, { headers, signal });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  if (!resp.body) throw new Error('ReadableStream not available');

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let headerSkipped = false;
  const allItems: NamedImageItem[] = [];

  const processLine = async (lineStr: string) => {
    const [link, title, description, wikidataTagRaw] = parseCsvLine(lineStr);
    const wikidataTag = normalizeWikidataTag(wikidataTagRaw || '');
    if (!isValidTopSightCsvItem(link, title, wikidataTag)) {
      console.warn('[TopSights][streamTopSightsCSV] skipped invalid CSV row', {
        line: lineStr,
        parsed: { link, title, wikidataTagRaw },
      });
      return;
    }

    const streamKey = `${String(link).trim().toLowerCase()}|${String(title).trim().toLowerCase()}|${wikidataTag}`;

    // 1) valid row arrived -> show skeleton card immediately.
    onRawItem?.({
      id: `pending:${streamKey}`,
      label: title,
      title,
      articleTitle: title,
      imageUrl: '',
      thumbnail: '',
      link,
      sourceWikiLang: viewportWikiLanguage,
      description,
      wikidataTag: wikidataTag || undefined,
      isEnriching: true,
      streamKey,
    });

    // 2) run enrichment in background and replace placeholder on success.
    const enriched = await enrichSight(
      { link, title, description, wikidataTag: wikidataTag || undefined },
      lat,
      lng,
      viewportWikiLanguage,
      userPreferredWikiLangCode || viewportWikiLanguage || 'en',
      signal,
    );

    const item = toNamedImageItem(enriched);
    if (item) {
      const readyItem: NamedImageItem = {
        ...item,
        isEnriching: false,
        streamKey,
      };
      allItems.push(readyItem);
      onItem(readyItem);
    } else {
      console.warn('[TopSights][streamTopSightsCSV] enrichment returned no displayable item', {
        title,
        link,
        wikidataTag: wikidataTag || null,
        streamKey,
      });
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      if (buffer.trim()) await processLine(buffer.trim());
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 1);
      if (!line) continue;
      if (!headerSkipped) {
        const lower = line.toLowerCase();
        if (lower.includes('link') && lower.includes('title')) {
          headerSkipped = true;
          continue;
        }
        headerSkipped = true;
      }
      await processLine(line);
    }
  }

  return allItems;
}
